import React, { useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, ScaleIcon, FilterIcon, DropletIcon, ClipboardListIcon, ChevronRightIcon } from 'lucide-react';
import { FlourOilWeighingProcess } from './FlourOilWeighingProcess';
interface FlourOilWeighingProps {
  onBackToOverview?: () => void;
}
export interface WeighingItem {
  id: string;
  name: string;
  type: 'flour' | 'oil';
  quantity: number;
  unit: string;
  orderId: string;
  orderName: string;
  status: 'pending' | 'in-progress' | 'completed';
}
export interface WeighingOrder {
  id: string;
  orderId: string;
  recipeName: string;
  items: WeighingItem[];
  status: 'pending' | 'in-progress' | 'completed';
  deadline: Date;
}
export function FlourOilWeighing({
  onBackToOverview
}: FlourOilWeighingProps) {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedOrder, setSelectedOrder] = useState<WeighingOrder | null>(null);
  const [selectedItem, setSelectedItem] = useState<WeighingItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'in-progress' | 'completed'>('all');
  // Beispieldaten für Mehl- und Ölverwiegungsaufträge
  const [weighingOrders, setWeighingOrders] = useState<WeighingOrder[]>([{
    id: 'WEIGH001',
    orderId: '1001',
    recipeName: 'Gustoria Paella',
    status: 'pending',
    deadline: new Date(new Date().setHours(10, 0, 0, 0)),
    items: [{
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      type: 'oil',
      quantity: 25.5,
      unit: 'kg',
      orderId: '1001',
      orderName: 'Gustoria Paella',
      status: 'pending'
    }, {
      id: 'R161215',
      name: 'Mehl',
      type: 'flour',
      quantity: 75.0,
      unit: 'kg',
      orderId: '1001',
      orderName: 'Gustoria Paella',
      status: 'pending'
    }]
  }, {
    id: 'WEIGH002',
    orderId: '1002',
    recipeName: 'Gustoria Risotto',
    status: 'pending',
    deadline: new Date(new Date().setHours(13, 0, 0, 0)),
    items: [{
      id: 'R151501',
      name: 'Rapsöl raffiniert',
      type: 'oil',
      quantity: 18.0,
      unit: 'kg',
      orderId: '1002',
      orderName: 'Gustoria Risotto',
      status: 'pending'
    }, {
      id: 'R161215',
      name: 'Mehl',
      type: 'flour',
      quantity: 90.0,
      unit: 'kg',
      orderId: '1002',
      orderName: 'Gustoria Risotto',
      status: 'pending'
    }]
  }]);
  // Gefilterte Aufträge basierend auf Suchbegriff und Status
  const filteredOrders = weighingOrders.filter(order => {
    const matchesSearch = order.recipeName.toLowerCase().includes(searchTerm.toLowerCase()) || order.orderId.includes(searchTerm) || order.items.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.id.includes(searchTerm));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const handleOrderSelect = (order: WeighingOrder) => {
    setSelectedOrder(order);
    setView('detail');
  };
  const handleBackToList = () => {
    setView('list');
    setSelectedOrder(null);
    setSelectedItem(null);
  };
  const handleItemSelect = (item: WeighingItem) => {
    setSelectedItem(item);
  };
  const handleWeighingComplete = (itemId: string) => {
    if (!selectedOrder) return;
    // Update the status for the completed item
    const updatedOrders = weighingOrders.map(order => {
      if (order.id === selectedOrder.id) {
        const updatedItems = order.items.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              status: 'completed' as const
            };
          }
          return item;
        });
        // Check if all items are completed
        const allCompleted = updatedItems.every(item => item.status === 'completed');
        return {
          ...order,
          items: updatedItems,
          status: allCompleted ? 'completed' as const : 'in-progress' as const
        };
      }
      return order;
    });
    setWeighingOrders(updatedOrders);
    // Update the selected order
    const updatedOrder = updatedOrders.find(o => o.id === selectedOrder.id);
    if (updatedOrder) {
      setSelectedOrder(updatedOrder);
      setSelectedItem(null); // Reset selected item after completion
    }
  };
  return <div className="container mx-auto" data-id="element-297">
      {/* Header */}
      <div className="mb-8" data-id="element-298">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-299">
          Mehl- und Ölverwiegung
        </h2>
        <div className="flex items-center justify-between" data-id="element-300">
          <p className="text-gray-600" data-id="element-301">
            Verwiegung von Mehl- und Ölkomponenten für die Produktion
          </p>
          {onBackToOverview}
        </div>
      </div>

      {/* Auftragsübersicht */}
      {view === 'list' ? <div className="bg-white rounded-xl shadow-md p-6" data-id="element-302">
          {/* Suchleiste */}
          <div className="flex flex-col md:flex-row gap-3 mb-6" data-id="element-303">
            <div className="relative flex-grow" data-id="element-304">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-305">
                <SearchIcon size={18} className="text-gray-400" data-id="element-306" />
              </div>
              <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Rezept oder Material suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-307" />
            </div>
            <div className="flex items-center" data-id="element-308">
              <FilterIcon size={18} className="text-gray-500 mr-2" data-id="element-309" />
              <select className="block w-full p-2 border border-gray-300 rounded-md" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} data-id="element-310">
                <option value="all" data-id="element-311">Alle Status</option>
                <option value="pending" data-id="element-312">Ausstehend</option>
                <option value="in-progress" data-id="element-313">In Bearbeitung</option>
                <option value="completed" data-id="element-314">Abgeschlossen</option>
              </select>
            </div>
          </div>

          {/* Auftragsliste */}
          <div className="space-y-4" data-id="element-315">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${order.status === 'completed' ? 'bg-green-50 border-green-200' : order.status === 'in-progress' ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`} onClick={() => handleOrderSelect(order)} data-id="element-316">
                  <div className="flex justify-between items-start" data-id="element-317">
                    <div data-id="element-318">
                      <h4 className="font-medium text-gray-900" data-id="element-319">
                        {order.recipeName}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-320">
                        Auftrags-Nr: #{order.orderId}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-321">
                        Bereitstellung bis:{' '}
                        {order.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                        Uhr
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-322">
                      {order.status === 'completed' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-323">
                          <CheckCircleIcon size={12} className="mr-1" data-id="element-324" />
                          Abgeschlossen
                        </span> : order.status === 'in-progress' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-325">
                          <PackageIcon size={12} className="mr-1" data-id="element-326" />
                          In Bearbeitung
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-327">
                          <ScaleIcon size={12} className="mr-1" data-id="element-328" />
                          Ausstehend
                        </span>}
                    </div>
                  </div>
                  <div className="mt-3" data-id="element-329">
                    <div className="text-sm text-gray-500 mb-1" data-id="element-330">
                      Fortschritt:{' '}
                      {order.items.filter(m => m.status === 'completed').length}{' '}
                      von {order.items.length} Materialien verwogen
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-331">
                      <div className="bg-[#8DC63F] h-2.5 rounded-full" style={{
                width: `${order.items.filter(m => m.status === 'completed').length / order.items.length * 100}%`
              }} data-id="element-332"></div>
                    </div>
                  </div>
                  {/* Materialien anzeigen */}
                  <div className="mt-3 flex flex-wrap gap-1" data-id="element-333">
                    {order.items.map(item => <span key={item.id} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.type === 'oil' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'} ${item.status === 'completed' ? 'opacity-60' : ''}`} data-id="element-334">
                        {item.type === 'oil' ? <DropletIcon size={12} className="mr-1" data-id="element-335" /> : <div size={12} className="mr-1" data-id="element-336" />}
                        {item.name}
                      </span>)}
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-200" data-id="element-337">
                <ScaleIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-338" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-339">
                  Keine Verwiegungsaufträge
                </h3>
                <p className="text-gray-500" data-id="element-340">
                  Für heute sind keine Verwiegungsaufträge geplant.
                </p>
              </div>}
          </div>
        </div> :
    // Detailansicht eines Auftrags
    <div className="bg-white rounded-xl shadow-md p-8" data-id="element-341">
          <button onClick={handleBackToList} className="flex items-center gap-2 mb-6 text-gray-600 hover:text-gray-800" data-id="element-342">
            <ArrowLeftIcon size={18} data-id="element-343" />
            <span data-id="element-344">Zurück zur Übersicht</span>
          </button>
          {selectedOrder && <>
              <div className="mb-6" data-id="element-345">
                <h3 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-346">
                  {selectedOrder.recipeName}
                </h3>
                <div className="flex items-center gap-4" data-id="element-347">
                  <p className="text-gray-600" data-id="element-348">
                    Auftrag #{selectedOrder.orderId}
                  </p>
                  <div className="flex items-center text-gray-600" data-id="element-349">
                    <ClipboardListIcon size={16} className="mr-1" data-id="element-350" />
                    <span data-id="element-351">
                      Fällig:{' '}
                      {selectedOrder.deadline.toLocaleTimeString('de-DE', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}{' '}
                      Uhr
                    </span>
                  </div>
                </div>
              </div>
              {selectedItem ? <FlourOilWeighingProcess item={selectedItem} onWeighingComplete={() => handleWeighingComplete(selectedItem.id)} onBack={() => setSelectedItem(null)} data-id="element-352" /> : <div data-id="element-353">
                  <h4 className="text-xl font-semibold text-gray-800 mb-6" data-id="element-354">
                    Zu verwiegende Materialien
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-355">
                    {selectedOrder.items.map(item => <div key={item.id} className={`border-2 rounded-lg p-6 ${item.status === 'completed' ? 'bg-green-50 border-green-200' : item.type === 'oil' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'} ${item.status !== 'completed' ? 'cursor-pointer hover:shadow-md' : ''}`} onClick={() => item.status !== 'completed' && handleItemSelect(item)} data-id="element-356">
                        <div className="flex justify-between items-center" data-id="element-357">
                          <div data-id="element-358">
                            <div className="flex items-center" data-id="element-359">
                              <div className={`p-3 rounded-full mr-4 ${item.type === 'oil' ? 'bg-amber-200' : 'bg-blue-200'}`} data-id="element-360">
                                {item.type === 'oil' ? <DropletIcon size={24} className="text-amber-700" data-id="element-361" /> : <div size={24} className="text-blue-700" data-id="element-362" />}
                              </div>
                              <div data-id="element-363">
                                <h5 className="text-lg font-semibold text-gray-900" data-id="element-364">
                                  {item.name}
                                </h5>
                                <p className="text-base text-gray-600" data-id="element-365">
                                  {item.quantity} {item.unit}
                                </p>
                                <p className="text-sm text-gray-500" data-id="element-366">
                                  Artikel-Nr: {item.id}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div data-id="element-367">
                            {item.status === 'completed' ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-368">
                                <CheckCircleIcon size={16} className="mr-1" data-id="element-369" />
                                Erledigt
                              </span> : <button className="px-4 py-2 bg-[#8DC63F] hover:bg-[#7AB929] text-white rounded-md text-base font-medium" onClick={e => {
                    e.stopPropagation();
                    handleItemSelect(item);
                  }} data-id="element-370">
                                Verwiegen
                              </button>}
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}
            </>}
        </div>}
    </div>;
}